import { queryOptions, useQuery } from '@tanstack/react-query'
import {
	AlertContextItemSchema,
	AlertContextValueSchema,
	AlertSchema,
	type Alert as AppSyncAlert,
	type ArchiveAlertResponse,
	ArchiveAlertResponseSchema,
	HailAlertSchema,
	LtzAlertSchema,
	PrecipitationAlertSchema,
	type SourceName,
	WeatherHazardAlertSchema,
	WindAlertSchema,
} from '@xweather/protect-types'
import { isNotNil, minBy } from 'es-toolkit'
import { gql } from 'graphql-request'
import { z } from 'zod'
import { createApiClient, createGraphQLClient } from '../../api/helpers.ts'
import { type Dayjs, dayjs } from '../../dayjs.ts'
import { env } from '../../env.ts'
import { useTimeline } from '../../features/timeline/timelineState.ts'
import { encodeImpersonation, type Impersonation, useImpersonation } from '../impersonation.ts'
import { ProtectionPriority, ProtectionType } from '../protections/types.ts'
import { API_GATEWAY_TIMEOUT } from '../protections/utils.ts'
import { useSubscription } from '../service.ts'
import type { AnyAlert, HailAlert, LightningAlert, PrecipitationAlert, Severity, WeatherHazardAlert, WindAlert } from './types.ts'
import { alertArchiveToSeverity, alertContextToSeverity, deduplicateWeatherHazardContexts, severityAsNumberMap } from './utils.ts'

const getAlerts = gql`
	query GetAlerts {
		getAlerts {
			items {
				vrn
				protectionVrn
				productVrn
				eventTime
				startTime
				previousStatus
				currentStatus
				alertContext {
					id
					time
					source
					alertContextValue
				}
				version
			}
		}
	}
`

// When we move from AppSync this can replace GraphQL client and we can rename the env var
const AlertEventApi = createApiClient({
	prefixUrl: env.API_ALERTS_GATEWAY,
	timeout: API_GATEWAY_TIMEOUT,
})

const getClient = (impersonation: Impersonation | undefined) => createGraphQLClient(env.API_ALERTS, impersonation)

export const alertsQuery = (impersonation: Impersonation | undefined, options: { enabled?: boolean }) =>
	queryOptions({
		queryKey: ['ALERTS', impersonation?.subscriptionId],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60,
		refetchIntervalInBackground: true, // To make audible alerts work while tab is in the background
		enabled: options.enabled,
		queryFn: async (): Promise<AnyAlert[]> => {
			try {
				const response: { getAlerts: { items: IncomingAlert[] } } = await getClient(impersonation).request(getAlerts)
				if (!response.getAlerts.items) return []

				return response.getAlerts.items
					.map((alert) => {
						const parsedAlert = IncomingAlertSchema.safeParse(alert)
						if (!parsedAlert.success) {
							console.error('Alert parsing error:', parsedAlert.error)
							return null
						}
						return toAlert(parsedAlert.data)
					})
					.filter((a) => isNotNil(a))
			} catch (error) {
				console.error('Error fetching alerts:', error)
				return []
			}
		},
	})

const fetchArchivedAlerts = async (
	START_TIME: string,
	END_TIME: string,
	source: SourceName,
	impersonation: Impersonation | undefined,
): Promise<IncomingArchivedAlert[]> => {
	try {
		const response = await AlertEventApi.get(`archive/${source}`, {
			searchParams: [
				['start', START_TIME],
				['end', END_TIME],
				['impersonation', encodeImpersonation(impersonation) ?? ''],
			],
		}).json<ArchiveAlertResponse[]>()
		return response.flatMap((item) => {
			const parsed = IncomingArchivedAlertSchema.safeParse(item)
			if (!parsed.success) {
				console.error('Historical alert parsing error:', parsed.error)
				return []
			}
			return parsed.data
		})
	} catch (error) {
		console.error('Error fetching historical alerts:', error)
		return []
	}
}

export const historicalAlertsQuery = (
	START_TIME: string,
	END_TIME: string,
	sources: SourceName[],
	impersonation: Impersonation | undefined,
	options: { enabled?: boolean } = { enabled: true },
) => {
	const sorted = [...sources].sort((a, b) => a.localeCompare(b))
	return queryOptions({
		queryKey: ['HISTORIC_ALERTS', START_TIME, END_TIME, sorted, impersonation?.subscriptionId],
		placeholderData: (previousData) => previousData?.filter((alert) => sorted.includes(alert.source)),
		queryFn: async (): Promise<IncomingArchivedAlert[]> => {
			const bySource = await Promise.all(sorted.map((source) => fetchArchivedAlerts(START_TIME, END_TIME, source, impersonation)))
			return bySource.flat()
		},
		refetchOnMount: false,
		enabled: options.enabled,
	})
}

// TODO: Remove once the hail CSV export is migrated to the `archive/{source}` endpoint
export const legacyHistoricalAlertsQuery = (
	START_TIME: string,
	END_TIME: string,
	source: SourceName,
	impersonation: Impersonation | undefined,
	options: { enabled?: boolean } = { enabled: true },
) =>
	queryOptions({
		queryKey: ['HISTORIC_ALERTS_LEGACY', START_TIME, END_TIME, source, impersonation?.subscriptionId],
		placeholderData: (previousData) => previousData,
		queryFn: async (): Promise<IncomingArchivedAlert[]> => {
			try {
				const response = await AlertEventApi.get('archive', {
					searchParams: [
						['start', START_TIME],
						['end', END_TIME],
						['source', source],
						['impersonation', encodeImpersonation(impersonation) ?? ''],
					],
				}).json<LegacyIncomingAlertArchive[]>()
				return response.flatMap((item) => {
					const parsed = LegacyIncomingAlertArchiveSchema.safeParse(item)
					if (!parsed.success) {
						console.error('Historical alert parsing error:', parsed.error)
						return []
					}
					// The legacy `archive` endpoint only ever serves HAIL_THREAT alerts
					const archivedAlert: IncomingArchivedAlert = {
						name: parsed.data.name,
						protectionVrn: parsed.data.protectionVrn,
						firstEventTime: parsed.data.firstEventTime,
						lastEventTime: parsed.data.lastEventTime,
						clearTime: parsed.data.clearTime,
						priority: parsed.data.priority,
						source: 'HAIL_THREAT',
						context: { bufferMeters: parsed.data.bufferMeters },
						severity: 'warning',
					}
					return [archivedAlert]
				})
			} catch (error) {
				console.error('Error fetching historical alerts:', error)
				return []
			}
		},
		refetchOnMount: false,
		enabled: options.enabled,
	})

export const useHistoricalAlerts = (startTime: Dayjs, endTime: Dayjs, sources: SourceName[]) => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	const clock = useTimeline((s) => s.clock)

	// The archive endpoint only has historic data, so fully-future ranges (eg. the "next 24 hours" preset)
	// are disabled entirely, and ranges that span both past and future (eg. "between 24 hours") are trimmed to now.
	const isFullyInFuture = startTime.isSameOrAfter(clock)
	const trimmedEndTime = endTime.isAfter(clock) ? clock : endTime
	const enabled = sources.length > 0 && !isFullyInFuture && subscription.features.includes('protections')

	const { data } = useQuery(
		historicalAlertsQuery(startTime.toISOString(), trimmedEndTime.toISOString(), sources, impersonation, { enabled }),
	)

	return sources.length === 0 ? [] : (data ?? [])
}

const toAlert = (alert: IncomingAlert): AnyAlert | undefined => {
	if (isHailAlert(alert)) return parseHailAlert(alert)
	if (isWeatherHazardAlert(alert)) return parseWeatherHazardAlert(alert)
	if (isWindAlert(alert)) return parseWindAlert(alert)
	if (isPrecipitationAlert(alert)) return parsePrecipitationAlert(alert)
	if (isLtzAlert(alert)) return parseLtzAlert(alert)
}

const parseHailAlert = (alert: IncomingAlert): HailAlert | undefined => {
	const result = HailAlertSchema.safeParse(alert)
	if (!result.success) return
	const severity = appsyncAlertToHighestSeverity(result.data)
	if (!severity) return
	return {
		protectionVrn: result.data.protectionVrn,
		type: 'hail',
		severity: severity,
		startTime: result.data.startTime,
		eventTime: result.data.eventTime,
		context: result.data.alertContext,
	}
}

const parseWeatherHazardAlert = (alert: IncomingAlert): WeatherHazardAlert | undefined => {
	// TODO: Remove this when we get alertContextValue sorted
	const alertWithDefaults = {
		...alert,
		alertContext: alert.alertContext.map((ctx) => ({
			...ctx,
			alertContextValue: {
				WEATHER_HAZARD_CERTAINTY: '',
				WEATHER_HAZARD_URGENCY: '',
				...ctx.alertContextValue,
			},
		})),
	}
	const result = WeatherHazardAlertSchema.safeParse(alertWithDefaults)
	if (!result.success) return
	const severity = appsyncAlertToHighestSeverity(result.data)
	if (!severity) return
	return {
		protectionVrn: result.data.protectionVrn,
		type: 'weatherHazard',
		severity: severity,
		startTime: result.data.startTime,
		context: deduplicateWeatherHazardContexts(result.data.alertContext),
	}
}

const parseWindAlert = (alert: IncomingAlert): WindAlert | undefined => {
	const result = WindAlertSchema.safeParse(alert)
	if (!result.success) return
	const severity = appsyncAlertToHighestSeverity(result.data)
	if (!severity) return
	return {
		protectionVrn: result.data.protectionVrn,
		type: 'wind',
		severity: severity,
		startTime: result.data.startTime,
		context: result.data.alertContext,
	}
}

// The alert engine never sends PRECIPITATION_PRIMARY_WEATHER_CODED, but the shared schema requires it.
// TODO: Remove once the alert schema is updated in @xweather/protect-types
const PrecipitationContextItemSchema = PrecipitationAlertSchema.shape.alertContext.element
const IncomingPrecipitationAlertSchema = PrecipitationAlertSchema.extend({
	alertContext: z.array(
		PrecipitationContextItemSchema.extend({
			alertContextValue: PrecipitationContextItemSchema.shape.alertContextValue.extend({
				PRECIPITATION_PRIMARY_WEATHER_CODED: z.string().default(''),
			}),
		}),
	),
})

const parsePrecipitationAlert = (alert: IncomingAlert): PrecipitationAlert | undefined => {
	const result = IncomingPrecipitationAlertSchema.safeParse(alert)
	if (!result.success) return
	const severity = appsyncAlertToHighestSeverity(result.data)
	if (!severity) return
	return {
		protectionVrn: result.data.protectionVrn,
		type: 'precipitation',
		severity: severity,
		startTime: result.data.startTime,
		eventTime: result.data.eventTime,
		context: result.data.alertContext,
	}
}

const parseLtzAlert = (alert: IncomingAlert): LightningAlert | undefined => {
	const result = LtzAlertSchema.safeParse(alert)
	if (!result.success) return
	const nearest = minBy(result.data.alertContext, (ctx) => dayjs(ctx.time).valueOf())
	if (!nearest) return
	return {
		protectionVrn: result.data.protectionVrn,
		type: ProtectionType.Lightning,
		severity: 'warning',
		startTime: result.data.startTime,
		firstTrigger: result.data.startTime,
		eventTime: result.data.eventTime,
		currentState: result.data.currentStatus,
		priority: ProtectionPriority.LightningAlarm,
		context: {
			LIGHTNING_THREAT_LEADTIME: Math.round(Number(nearest.alertContextValue.LTZ_HORIZON_MIN)),
			forecastTime: nearest.time,
			directionFromAsset: nearest.alertContextValue.LTZ_DIR_FROM_ASSET,
		},
	}
}

const IncomingAlertSchema = AlertSchema.pick({
	vrn: true,
	protectionVrn: true,
	productVrn: true,
	eventTime: true,
	startTime: true,
	previousStatus: true,
	currentStatus: true,
	alertContext: true,
	version: true,
}).extend({
	alertContext: z.array(
		AlertContextItemSchema.extend({
			alertContextValue: z.string().transform((val) => AlertContextValueSchema.parse(JSON.parse(val))),
		}),
	),
})

export type IncomingAlert = z.infer<typeof IncomingAlertSchema>

const LegacyIncomingAlertArchiveSchema = z.object({
	name: z.string().optional(),
	protectionVrn: z.string(),
	bufferMeters: z.number().optional(),
	firstEventTime: z.iso.datetime(),
	lastEventTime: z.iso.datetime(),
	clearTime: z.iso.datetime().optional(),
	priority: z.number().optional(),
})
export type LegacyIncomingAlertArchive = z.infer<typeof LegacyIncomingAlertArchiveSchema>

const IncomingArchivedAlertSchema = ArchiveAlertResponseSchema.transform((data) => ({
	...data,
	severity: alertArchiveToSeverity(data),
}))
export type IncomingArchivedAlert = z.infer<typeof IncomingArchivedAlertSchema>

const appsyncAlertToHighestSeverity = (alert: AppSyncAlert): Severity | undefined => {
	let highestSeverity: Severity | undefined
	for (const context of alert.alertContext) {
		if (!context.source) continue
		const severity = alertContextToSeverity(context)
		if (!severity) continue
		if (!highestSeverity || severityAsNumberMap[severity] > severityAsNumberMap[highestSeverity]) {
			highestSeverity = severity
		}
	}
	return highestSeverity
}

const isHailAlert = (alert: IncomingAlert) => alert.alertContext.some((ctx) => ctx.source === 'HAIL_THREAT')
const isWeatherHazardAlert = (alert: IncomingAlert) => alert.alertContext.some((ctx) => ctx.source === 'WEATHER_HAZARD')
const isWindAlert = (alert: IncomingAlert) => alert.alertContext.some((ctx) => ctx.source === 'ERG_ECMWF_HRES')
const isPrecipitationAlert = (alert: IncomingAlert) => alert.alertContext.some((ctx) => ctx.source === 'XWEATHER_FORECASTS')
const isLtzAlert = (alert: IncomingAlert) => alert.alertContext.some((ctx) => ctx.source === 'LIGHTNING_THREAT')
